import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllEmps,
} from "../../redux/empSlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Helmet } from "react-helmet-async";

const EmpReport = () => {
  const dispatch = useDispatch();
  const { emps, loading } = useSelector((state) => state.emp);

  // Date range states
  const [dateRange, setDateRange] = useState("thisMonth");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEmps, setFilteredEmps] = useState([]);

  // Load employees
  useEffect(() => {
    dispatch(getAllEmps());
  }, [dispatch]);

  // Filter employees
  useEffect(() => {
    let filtered = [...emps];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.number?.includes(searchQuery)
      );
    }

    // Filter by date range (created date)
    if (dateRange === "thisMonth") {
      const today = new Date();
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      filtered = filtered.filter((emp) => {
        const empDate = new Date(emp.createdAt);
        return empDate >= thisMonthStart && empDate <= thisMonthEnd;
      });
    } else if (dateRange === "lastMonth") {
      const today = new Date();
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      filtered = filtered.filter((emp) => {
        const empDate = new Date(emp.createdAt);
        return empDate >= lastMonthStart && empDate <= lastMonthEnd;
      });
    } else if (dateRange === "custom" && startDate && endDate) {
      filtered = filtered.filter((emp) => {
        const empDate = new Date(emp.createdAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return empDate >= start && empDate <= end;
      });
    }

    setFilteredEmps(filtered);
  }, [emps, dateRange, startDate, endDate, searchQuery]);

  // Generate PDF
  const generatePDF = () => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.text("SHREE HAREKRISHNA GAAJ CENTER", 105, 15, { align: "center" });
    doc.setFontSize(14);
    doc.text("Employee Report", 105, 25, { align: "center" });

    // Report details
    doc.setFontSize(10);
    let dateInfo = "";
    if (dateRange === "thisMonth") {
      const today = new Date();
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      dateInfo = `Date: ${thisMonthStart.toLocaleDateString()} to ${thisMonthEnd.toLocaleDateString()}`;
    } else if (dateRange === "lastMonth") {
      const today = new Date();
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      dateInfo = `Date: ${lastMonthStart.toLocaleDateString()} to ${lastMonthEnd.toLocaleDateString()}`;
    } else if (dateRange === "custom" && startDate && endDate) {
      dateInfo = `Date: ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
    }

    doc.text(dateInfo, 14, 35);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 40);
    doc.text(`Total Employees: ${filteredEmps.length}`, 14, 45);

    // Table data
    const tableData = filteredEmps.map((emp, index) => [
      index + 1,
      emp.name || "N/A",
      emp.number || "N/A",
      emp.user?.name || "N/A",
      new Date(emp.createdAt).toLocaleDateString(),
    ]);

    // Create table
    autoTable(doc, {
      startY: 50,
      head: [["NO", "NAME", "NUMBER", "CREATED BY", "DATE"]],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [102, 126, 234] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    // Save PDF
    const fileName = `Employee_Report_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  return (
    <>
      <Helmet>
        <title>Employee Report - RTS Books</title>
      </Helmet>
      <style>{`
        .emp-report-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .emp-report-wrapper {
          max-width: 1600px;
          margin: 0 auto;
        }

        .emp-report-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .emp-report-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .emp-report-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .emp-report-filters {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: flex-end;
        }

        .emp-report-filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .emp-report-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #334155;
        }

        .emp-report-select,
        .emp-report-input {
          padding: 10px 14px;
          border: 2px solid #e1e5e9;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
          min-width: 150px;
        }

        .emp-report-select:focus,
        .emp-report-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .emp-report-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .emp-report-btn-success {
          background: #10b981;
          color: white;
        }

        .emp-report-btn-success:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .emp-report-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .emp-report-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .emp-report-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .emp-report-table th {
          padding: 14px;
          text-align: left;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .emp-report-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #e1e5e9;
        }

        .emp-report-table tbody tr:hover {
          background: #f8f9fa;
        }

        .emp-report-table tbody tr:last-child td {
          border-bottom: none;
        }

        .emp-report-summary {
          background: #f0fdf4;
          border: 2px solid #86efac;
          border-radius: 12px;
          padding: 20px;
          margin-top: 20px;
        }

        .emp-report-summary-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #059669;
          margin-bottom: 15px;
        }

        .emp-report-summary-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #dcfce7;
        }

        .emp-report-summary-row:last-child {
          border-bottom: none;
          font-weight: 700;
          font-size: 1.1rem;
          color: #059669;
        }

        .emp-report-summary-label {
          color: #166534;
          font-weight: 600;
        }

        .emp-report-summary-value {
          color: #059669;
          font-weight: 700;
        }

        @media (max-width: 768px) {
          .emp-report-container {
            padding: 15px;
          }

          .emp-report-title {
            font-size: 2rem;
          }

          .emp-report-card {
            padding: 20px;
          }

          .emp-report-filters {
            flex-direction: row;
          }

          .emp-report-select,
          .emp-report-input {
            width: 100%;
            min-width: 100%;
          }

          .emp-report-table {
            font-size: 0.85rem;
          }

          .emp-report-table th,
          .emp-report-table td {
            padding: 10px 8px;
          }
        }

        @media (max-width: 480px) {
          .emp-report-title {
            font-size: 1.5rem;
          }

          .emp-report-card {
            padding: 15px;
          }

          .emp-report-table {
            font-size: 0.8rem;
          }

          .emp-report-table th,
          .emp-report-table td {
            padding: 8px 6px;
          }
        }
      `}</style>

      <div className="emp-report-container">
        <div className="emp-report-wrapper">
          <div className="emp-report-header">
            <h1 className="emp-report-title">Employee Report</h1>
          </div>

          <div className="emp-report-card">
            <div className="emp-report-filters">
              <div className="emp-report-filter-group">
                <label className="emp-report-label">Date Range</label>
                <select
                  className="emp-report-select"
                  value={dateRange}
                  onChange={(e) => {
                    setDateRange(e.target.value);
                    if (e.target.value !== "custom") {
                      setStartDate("");
                      setEndDate("");
                    }
                  }}
                >
                  <option value="thisMonth">This Month</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {dateRange === "custom" && (
                <>
                  <div className="emp-report-filter-group">
                    <label className="emp-report-label">Start Date</label>
                    <input
                      type="date"
                      className="emp-report-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="emp-report-filter-group">
                    <label className="emp-report-label">End Date</label>
                    <input
                      type="date"
                      className="emp-report-input"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="emp-report-filter-group">
                <label className="emp-report-label">Search</label>
                <input
                  type="text"
                  className="emp-report-input"
                  placeholder="Search by name or number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                className="emp-report-btn emp-report-btn-success"
                onClick={generatePDF}
                disabled={filteredEmps.length === 0}
              >
                📥 Download PDF
              </button>
            </div>

            {loading && <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>}

            {!loading && filteredEmps.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                No employees found for the selected criteria
              </div>
            )}

            {!loading && filteredEmps.length > 0 && (
              <>
                <div className="emp-report-table-wrapper">
                  <table className="emp-report-table">
                    <thead>
                      <tr>
                        <th>NO</th>
                        <th>NAME</th>
                        <th>NUMBER</th>
                        <th>CREATED BY</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmps.map((emp, index) => (
                        <tr key={emp._id}>
                          <td>{index + 1}</td>
                          <td>{emp.name}</td>
                          <td>{emp.number}</td>
                          <td>{emp.user?.name || "N/A"}</td>
                          <td>{new Date(emp.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="emp-report-summary">
                  <div className="emp-report-summary-title">Summary</div>
                  <div className="emp-report-summary-row">
                    <span className="emp-report-summary-label">Total Employees:</span>
                    <span className="emp-report-summary-value">{filteredEmps.length}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmpReport;

